import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('resetPassword.invalidToken'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    // Validate password complexity
    if (!/[A-Z]/.test(password)) {
      setError(t('resetPassword.needUppercase'));
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError(t('resetPassword.needLowercase'));
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError(t('resetPassword.needNumber'));
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/;'`~]/.test(password)) {
      setError(t('resetPassword.needSpecialChar'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    if (!token) {
      setError(t('resetPassword.invalidToken'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || t('resetPassword.resetFailed'));
      }
    } catch (err) {
      setError(t('errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('resetPassword.successTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('resetPassword.successMessage')}
            </p>
            <p className="text-sm text-gray-500">
              {t('resetPassword.redirecting')}
            </p>
          </div>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('resetPassword.invalidLinkTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('resetPassword.invalidLinkMessage')}
            </p>
            <Link
              to="/forgot-password"
              className="inline-block bg-black text-white py-3 px-6 rounded-full hover:bg-gray-800 transition font-medium"
            >
              {t('resetPassword.requestNewLink')}
            </Link>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8 overflow-y-auto">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center mb-6 sm:mb-8">
            <img src="/tahc-logo-2026.jpg" alt="tahc" className="w-24 h-24 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6 object-contain" />
            <p className="text-gray-600 text-sm sm:text-base">
              {t('resetPassword.enterNewPassword')}
            </p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {t('resetPassword.newPassword')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder={t('resetPassword.passwordPlaceholder')}
                required
                minLength={12}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('resetPassword.passwordRequirements')}</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              {t('resetPassword.confirmPassword')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder={t('resetPassword.confirmPlaceholder')}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white py-3 px-4 rounded-full hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-gray-900 hover:text-gray-700 text-sm font-medium underline"
          >
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}
